import usePrepaid from '@/hooks/usePrepaid'
import useServicePriceTotal from '@/hooks/useServicePriceTotal'
import { COMMITMENT_PREPAID } from '@/modules/Contract/constants'
import {
    Contract,
    ProductResponse,
    ServiceConfigOption,
} from '@/modules/Contract/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, Select, Typogaphy } from 'vnetwork-ui'
import ServiceTimes from '../../Base/ServiceTimes'
import VMInstance from './VMInstance'
import VMOverview from './VMInstance/VMOverview'

export interface VMInstanceConfigOption {
    name: string
    title: string
    price?: number
}

interface VCloudProps {
    index: number
    product: ProductResponse
    isPOC?: boolean
    isAddon?: boolean
    showOverview?: boolean
}

const VCloud: React.FC<VCloudProps> = ({
    index,
    product,
    isPOC = false,
    isAddon = false,
    showOverview = false,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()

    const [vmPackage, setVmPackage] = useState<'predefined' | 'custom'>(
        'predefined'
    )
    const service_configoptions = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].service_configoptions` as Path<Contract>,
    }) as ServiceConfigOption[]

    useServicePriceTotal({
        control: formControl.control,
        index,
        service_configoptions,
        setValue: formControl.setValue,
        servicePriceModel:
            formControl.getValues(
                `order.products.[${index}].service_price_model` as Path<Contract>
            ) || 'package',
    })

    usePrepaid({ index })

    // Initialize service config options when product changes
    useEffect(() => {
        if (
            !product?.product_config_options?.length ||
            service_configoptions?.length > 0
        ) {
            return // Don't reinitialize if already has data
        }

        // Create default VM Instance item only
        const defaultVMInstance: Partial<
            ServiceConfigOption & { title?: string; vmIndex: number }
        > = {
            name: '', // Will be selected from config options
            commitment_value: 1,
            commitment_price_per_unit: 0,
            commitment_price_total: 0,
            commitment_time: 1,
            unit: 'package',
            title: 'vm-instance', // Mark as VM Instance type
            commitment_prepaid: true,
            statementable: false,
            type: 'vm-instance',
            vmIndex: 0,
        }

        const defaultVMStorage: Partial<
            ServiceConfigOption & { title?: string; vmIndex: number }
        > = {
            name: 'storage', // Will be selected from config options
            commitment_value: 1,
            commitment_price_per_unit: 0,
            commitment_price_total: 0,
            commitment_time: 1,
            unit: 'package',
            title: 'vm-other', // Mark as VM Instance type
            commitment_prepaid: true,
            statementable: false,
            type: 'vm-other',
            vmIndex: 1,
        }

        formControl.setValue(
            `order.products.[${index}].service_configoptions` as Path<Contract>,
            [defaultVMInstance, defaultVMStorage]
        )
    }, [
        product?.product_config_options,
        service_configoptions?.length,
        formControl,
        index,
    ])

    // Config options for VMInstance component
    const vmConfigOptions = useMemo(() => {
        const options =
            product?.product_config_options?.map((option) => ({
                name: option.name,
                title: option.title, // Use name as title for now, could be enhanced
                price: option.price || 0,
            })) || []
        return options
    }, [product?.product_config_options])

    // Handle VMInstance data updates
    const handleVMInstanceUpdate = useCallback(
        (updatedData: ServiceConfigOption[]) => {
            formControl.setValue(
                `order.products.[${index}].service_configoptions` as Path<Contract>,
                updatedData
            )
            // Trigger form validation after update
            formControl.trigger(
                `order.products.[${index}].service_configoptions` as Path<Contract>
            )
            formControl.trigger(
                `order.products.[${index}].service_price_register` as Path<Contract>
            )
        },
        [formControl, index]
    )

    return (
        <div className="space-y-4">
            {showOverview && <VMOverview data={service_configoptions || []} />}

            {/* VM Package Type Toggle */}
            <div className="space-y-2" data-tour="vcloud-package-type">
                <Select
                    size="small"
                    label09={t('vm_instance_config_type')}
                    options={[
                        {
                            label: t('predefined_packages'),
                            value: 'predefined',
                        },
                        {
                            label: t('custom_quota'),
                            value: 'custom',
                        },
                    ]}
                    allowClear={false}
                    value={vmPackage}
                    onChange={(value) => {
                        const newVmPackage = value as 'predefined' | 'custom'
                        setVmPackage(newVmPackage)

                        // Reset service_configoptions to have only 1 VM instance with quantity = 1
                        const resetVMInstance: Partial<
                            ServiceConfigOption & {
                                title?: string
                                vmIndex: number
                            }
                        > = {
                            name: '', // Will be selected from config options or custom name
                            commitment_value: 1,
                            commitment_price_per_unit: 0,
                            commitment_price_total: 0,
                            commitment_time: 1,
                            unit:
                                newVmPackage === 'custom'
                                    ? 'custom'
                                    : 'package',
                            title: 'vm-instance',
                            commitment_prepaid: true,
                            statementable: false,
                            vmIndex: 0,
                            type: 'vm-instance',
                            // Custom fields for custom mode
                            ...(newVmPackage === 'custom' && {
                                vcpu: 0,
                                ram: 0,
                            }),
                        }

                        formControl.setValue(
                            `order.products.[${index}].service_configoptions` as Path<Contract>,
                            [resetVMInstance]
                        )
                    }}
                />
                {/* Highlight message - luôn hiển thị để hướng dẫn người dùng */}
                <div
                    className={`border-l-4 rounded-r-md p-3 shadow-sm bg-amber-50 border-amber-500`}
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg
                                className={`h-5 w-5 text-amber-600`}
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <Typogaphy
                                size="small"
                                className={`font-medium text-amber-800`}
                            >
                                {t('custom_quota_info')}
                            </Typogaphy>
                            <Typogaphy
                                size="small"
                                className={`mt-1 text-amber-700`}
                            >
                                {t('custom_quota_hint')}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            </div>
            <div data-tour="vcloud-instances">
                <VMInstance
                    servicePriceModel={formControl.getValues(
                        `order.products.[${index}].service_price_model` as Path<Contract>
                    )}
                    isPOC={isPOC}
                    vmName={`order.products.[${index}].service_configoptions`}
                    data={service_configoptions || []}
                    configOptions={vmConfigOptions}
                    onUpdate={handleVMInstanceUpdate}
                    vmPackageType={vmPackage}
                />
            </div>

            {/* Prepaid selection - moved to separate section for clarity */}
            {!isPOC && (
                <div data-tour="vcloud-prepaid">
                    <GridHookForm
                        subForm
                        actions={[]}
                        data={[
                            {
                                formItemProps: {
                                    name: `order.products.[${index}].prepaid` as Path<Contract>,
                                    type: 'select',
                                    options: COMMITMENT_PREPAID(t),
                                    label: t('commitment_prepaid'),
                                    allowClear: false,
                                    placeholder: t('commitment_prepaid'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                formItemProps: {
                                    name: `order.products.[${index}].service_price_register` as Path<Contract>,
                                    type: 'number',
                                    label: t('service_price_register'),
                                    placeholder: t('service_price_register'),
                                },
                                className: 'col-span-2',
                            },
                        ]}
                        formControl={formControl}
                    />
                </div>
            )}

            {!isPOC && <ServiceTimes index={index} isAddon={isAddon} />}
        </div>
    )
}

export default VCloud
